import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import LockClockRoundedIcon from '@mui/icons-material/LockClockRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import { adminLogin } from '../../services/adminAuthService';
import './AdminLogin.scss';

const adminHighlights = [
  {
    icon: <InsightsRoundedIcon fontSize='small' />,
    title: 'Live store visibility',
    description: 'Track orders, inventory movement, and campaign performance in one place.',
  },
  {
    icon: <VerifiedUserRoundedIcon fontSize='small' />,
    title: 'Role-aware access',
    description: 'Built for operators, managers, and owners who need fast secure entry.',
  },
  {
    icon: <LockClockRoundedIcon fontSize='small' />,
    title: 'Protected sessions',
    description: 'Designed for controlled access with recovery and session support cues.',
  },
];

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formValues, setFormValues] = useState({
    email: '',
    password: '',
  });

  const redirectTo = location.state?.from?.pathname || '/myadmin/dashboard';

  const handleFieldChange = (field) => (event) => {
    setFormValues((current) => ({
      ...current,
      [field]: event.target.value,
    }));

    setFieldErrors((current) => ({
      ...current,
      [field]: '',
    }));

    setSubmitError(null);
  };

  const validateForm = () => {
    const errors = {};

    if (!formValues.email.trim()) {
      errors.email = 'Please enter the admin email.';
    }

    if (!formValues.password) {
      errors.password = 'Please enter the password.';
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setSubmitError({
        message: 'Please fill in the required admin credentials.',
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setFieldErrors({});

    try {
      await adminLogin(formValues);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      setSubmitError(error);
      setFieldErrors(error.validationErrors || {});
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className='admin-login-page'>
      <Box className='admin-login-shell'>
        <Box className='admin-login-panel admin-login-panel--brand'>
          <Chip
            className='admin-login-chip'
            icon={<ShieldRoundedIcon />}
            label='Admin Portal'
          />

          <Typography className='admin-login-kicker'>Pappad operations</Typography>
          <Typography variant='h2' className='admin-login-title'>
            Securely access the control room from desktop, tablet, or mobile.
          </Typography>
          <Typography className='admin-login-subtitle'>
            Sign in to manage products, orders, inventory, coupons, and the
            rest of the store operations from one protected admin workspace.
          </Typography>

          <Stack spacing={2.25} className='admin-login-highlights'>
            {adminHighlights.map((item) => (
              <Box key={item.title} className='admin-login-highlight'>
                <Box className='admin-login-highlightIcon'>{item.icon}</Box>
                <Box>
                  <Typography className='admin-login-highlightTitle'>
                    {item.title}
                  </Typography>
                  <Typography className='admin-login-highlightDescription'>
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box className='admin-login-panel admin-login-panel--form'>
          <Box className='admin-login-formWrap'>
            <Typography variant='h4' className='admin-login-formTitle'>
              Sign in to admin
            </Typography>
            <Typography className='admin-login-formSubtitle'>
              Use your authorized admin email and password to continue.
            </Typography>

            <Alert severity='info' className='admin-login-alert'>
              Admin access is restricted to approved team members only.
            </Alert>

            {submitError ? (
              <Alert severity='error' className='admin-login-alert'>
                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                  Admin sign-in failed
                </Typography>
                <Typography>{submitError.message}</Typography>
              </Alert>
            ) : null}

            <Stack
              component='form'
              spacing={2.25}
              className='admin-login-form'
              onSubmit={handleSubmit}
            >
              <TextField
                fullWidth
                label='Work email'
                type='email'
                placeholder='admin@papad.com'
                value={formValues.email}
                onChange={handleFieldChange('email')}
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
              />

              <TextField
                fullWidth
                label='Password'
                type={showPassword ? 'text' : 'password'}
                placeholder='Enter your password'
                value={formValues.password}
                onChange={handleFieldChange('password')}
                error={Boolean(fieldErrors.password)}
                helperText={fieldErrors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        edge='end'
                        onClick={() => setShowPassword((value) => !value)}
                        aria-label={
                          showPassword ? 'Hide password' : 'Show password'
                        }
                      >
                        {showPassword ? (
                          <VisibilityOffRoundedIcon />
                        ) : (
                          <VisibilityRoundedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box className='admin-login-formRow'>
                <FormControlLabel
                  control={<Checkbox defaultChecked color='primary' />}
                  label='Keep me signed in'
                />
                <Link component={RouterLink} to='/' underline='hover' className='admin-login-link'>
                  Back to store
                </Link>
              </Box>

              <Button
                type='submit'
                variant='contained'
                size='large'
                disableElevation
                className='admin-login-submit'
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Enter admin panel'}
              </Button>
            </Stack>

            <Divider className='admin-login-divider'>Security note</Divider>

            <Typography className='admin-login-securityText'>
              Only use this portal on trusted devices. Admin APIs use the
              stored `admin_token` for protected requests after login.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
